'use client';

import Link from 'next/link';
import { Menu } from 'lucide-react';
import { useParams, usePathname } from 'next/navigation';

import { MenuItems } from '#/data/menu';
import { useLocale, useT } from '#/i18n/client';
import { ROUTES } from '#/data/constants';
import { UpdateNewBadge } from './update-new-badge';
import { cn } from '#/lib/utils';
import { Button } from '#/components/ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '#/components/ui/sheet';

interface MobileMainNavProps {
  permissions: string[];
  system?: boolean;
  latestUpdateAt?: string | null;
}

export function MobileMainNav({
  permissions,
  system,
  latestUpdateAt
}: MobileMainNavProps) {
  const pathname = usePathname();
  const params = useParams();
  const locale = useLocale();
  const t = useT('common');
  const routes = MenuItems(params, pathname || '', locale).filter(
    (route) =>
      (system && !route.hide) ||
      route.default === true ||
      route.permissions?.some((permission) =>
        permissions.includes(permission.toString())
      )
  );

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="md:hidden"
          aria-label={t('nav.openMenu')}
        >
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-[min(20rem,85vw)] gap-0 overflow-y-auto p-0"
      >
        <SheetHeader className="border-b border-[#104573]/10 px-5 py-4 text-left">
          <SheetTitle>{t('nav.menu')}</SheetTitle>
        </SheetHeader>
        <nav className="flex flex-col p-3">
          {routes.map((route) => {
            const href =
              system || route.default
                ? route.href
                : route.children?.find((child) =>
                    child.permissions.some((permission) =>
                      permissions.includes(permission)
                    )
                  )?.href;

            if (!href) return null;

            // 헤더 드롭다운에 대응하는 자리. 시트에서는 접지 않고 하위 도구를
            // 그대로 들여써서 한눈에 보이게 한다.
            if (route.dropdown && route.children?.length) {
              return (
                <div key={route.href} className="mb-1">
                  <div className="px-3 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    사용해 보기
                  </div>
                  {route.children.map((child) => (
                    <SheetClose asChild key={child.href}>
                      <Link
                        href={child.href}
                        className={cn(
                          'block rounded-md px-3 py-3 text-base font-medium transition-colors',
                          child.active
                            ? 'bg-[#104573]/10 text-[#104573] dark:bg-[#60a3d9]/15 dark:text-[#60a3d9]'
                            : 'text-muted-foreground hover:bg-[#104573]/5 hover:text-[#104573]'
                        )}
                      >
                        {child.label}
                      </Link>
                    </SheetClose>
                  ))}
                </div>
              );
            }

            return (
              <SheetClose asChild key={route.href}>
                <Link
                  href={href}
                  className={cn(
                    'rounded-md px-3 py-3 text-base font-medium transition-colors',
                    route.active
                      ? 'bg-[#104573]/10 text-[#104573] dark:bg-[#60a3d9]/15 dark:text-[#60a3d9]'
                      : 'text-muted-foreground hover:bg-[#104573]/5 hover:text-[#104573]'
                  )}
                >
                  {route.label}
                  {route.href === ROUTES.UPDATE_LOGS && latestUpdateAt && (
                    <UpdateNewBadge latestDate={latestUpdateAt} />
                  )}
                </Link>
              </SheetClose>
            );
          })}
        </nav>
      </SheetContent>
    </Sheet>
  );
}
